/**
 * What a round *is* once it has left the barrel: how far it can carry, how big
 * the thing it might run into is, and how much comes off when it does.
 *
 * Where it goes is `gunnery.ts` (the gun line) and whether it got there is
 * `collide.ts` (pure geometry). Nothing in this module rolls for a hit, because
 * the original does not: `RB.EXE`'s `collide.c` intersects the round's path
 * with the target's extent and that is the whole test. The single die it throws
 * is the one below — **how much damage a hit does**, rolled after the geometry
 * has already said it connected.
 *
 * That is the shape the port used to have backwards. The previous model sized a
 * random cone off the `.DAT` gun block's BAM16 fields, sampled a scatter angle
 * per round, and compared it against the target's angular radius — a hit
 * *probability* dressed as ballistics. The fields are the mount's traverse arcs
 * (`traverse.ts`), rounds fly dead straight, and a burst that misses missed
 * geometrically.
 *
 * What survives from the old model, because it is real and independent of how
 * hits are decided:
 *
 * - **Damage falloff** with range, halving at `gunDamageHalfRangeM`, and a hard
 *   `gunMaxRangeM` past which a round is spent and simply stops existing.
 * - **Gun effectiveness** (`.DAT` offset 206, 800/900/1024/1300) as a damage
 *   multiplier against the 1024 class.
 * - The target's extent: a sphere of radius
 *   `targetSizeRaw × targetRadiusMetresPerUnit`, from `.DAT` offset 140 — the
 *   one size field the game actually stores.
 */

import type { RngBank, RngStreamName } from "../flight/rng.ts";
import type { Tunables } from "../flight/tunables.ts";

/** The radius of the sphere `collide.ts` shoots at, from `.DAT` offset 140. */
export function targetRadiusM(targetSizeRaw: number, tunables: Tunables): number {
  return Math.max(0, targetSizeRaw) * tunables.targetRadiusMetresPerUnit;
}

/** Angular radius the target subtends at `rangeM` — for the HUD and for tuning, not for hit resolution. */
export function targetAngularRadiusRad(targetSizeRaw: number, rangeM: number, tunables: Tunables): number {
  return Math.atan2(targetRadiusM(targetSizeRaw, tunables), Math.max(rangeM, 1));
}

/** Fraction of point-blank damage a round still carries at `rangeM`. */
export function damageFalloff(rangeM: number, tunables: Tunables): number {
  if (rangeM >= tunables.gunMaxRangeM) return 0;
  const half = Math.max(tunables.gunDamageHalfRangeM, 1);
  return 1 / (1 + Math.max(0, rangeM) / half);
}

/**
 * Health one round removes on a hit before the magnitude roll: the shooting
 * gun's effectiveness field (`.DAT` offset 206) against the 1024 reference
 * class, times the range falloff.
 */
export function roundDamage(damageRaw: number, rangeM: number, tunables: Tunables): number {
  const effectiveness = damageRaw / Math.max(tunables.gunEffectivenessReference, 1);
  return tunables.gunDamagePerRound * effectiveness * damageFalloff(rangeM, tunables);
}

/**
 * The one die the original throws in a gunfire exchange: a uniform multiplier
 * on an already-scored hit, symmetric about 1 so the mean is exactly
 * `roundDamage`. One draw from the `gunnery` stream — or from whichever stream
 * the shooter belongs to, which is `"airship"` for a Zeppelin's gunners and
 * follows `allocateHit`'s existing parameter for exactly the same reason
 * (AD-007a: one subsystem's draws must not move another's).
 */
export function rollDamage(
  rng: RngBank,
  base: number,
  tunables: Tunables,
  stream: RngStreamName = "gunnery",
): number {
  const spread = Math.max(0, Math.min(1, tunables.gunDamageRandomSpread));
  return base * (1 - spread + 2 * spread * rng.nextFloat(stream));
}
